import { useEffect } from "react";

/**
 * JsonLd — Injects a JSON-LD structured data script into <head>.
 *
 * Uses an `id` attribute (data-jsonld-id) to prevent duplicates when the
 * component re-renders or when multiple JsonLd blocks are declared with the
 * same @id (e.g. the global LocalBusiness present in index.html should not be
 * duplicated by a per-page emit).
 *
 * Props:
 *  - data: JavaScript object (@context + @type + @id + payload)
 *  - id:   unique DOM id used to deduplicate (defaults to @id or @type)
 */
export default function JsonLd({ data, id }) {
    useEffect(() => {
        if (!data) return;
        const jsonldId =
            id ||
            data["@id"] ||
            (typeof data["@type"] === "string"
                ? data["@type"]
                : Array.isArray(data["@type"])
                ? data["@type"].join("-")
                : "jsonld");

        // Remove any existing script with the same id (static or previous mount).
        const selector = `script[type="application/ld+json"][data-jsonld-id="${CSS.escape(
            jsonldId
        )}"]`;
        document.querySelectorAll(selector).forEach((el) => el.remove());

        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-jsonld-id", jsonldId);
        s.textContent = JSON.stringify(data);
        document.head.appendChild(s);

        return () => {
            s.remove();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(data), id]);

    return null;
}
